import React, {useState, useEffect, useCallback} from 'react';
import './_fields.scss';
import {Loader, Pagination} from 'components';
import {FaRegEye} from 'react-icons/fa';
import Filters from 'components/filters/Filters';
import SortBy from 'components/sortBy/SortBy';
import SearchBar from 'components/searchBar/SearchBar';
import {routes} from 'common';
import {useNavigate} from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { generalExtraActions } from 'reduxStore/actions';

const filterOptions = [
  {label: 'Active', value: 'active'},
  {label: 'Inactive', value: 'inactive'},
];

const sortOptions = [
  {value: '-created', label: 'Date Added (Newest)'},
  {value: 'created', label: 'Date Added (Oldest)'},
  {value: '-modified', label: 'Date Modified (Newest)'},
  {value: 'modified', label: 'Date Modified (Oldest)'},
];

function Fields() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { fields, fieldsLoading, fieldsError, fieldsPagination } = useSelector((state) => state.general);

  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Reset page to 1 when filter, search, or sort changes
  useEffect(() => {
    setCurrentPage(1);
  }, [filter, search, sortBy, itemsPerPage]);

  // Build API parameters
  const buildApiParams = useCallback(() => {
    const params = {
      page: currentPage,
      page_size: itemsPerPage,
    };
    if (sortBy) {
      params.ordering = sortBy;
    }
    if (filter !== 'all') {
      if (filter === 'active') params.is_active = true;
      else if (filter === 'inactive') params.is_active = false;
    }
    if (search) {
      params.search = search;
    }
    return params;
  }, [currentPage, itemsPerPage, sortBy, filter, search]);

  const handleFilter = (status) => {
    setFilter(prev => (prev === status ? 'all' : status));
  };

  const handleSearch = (_search) => {
    setSearch(_search);
  };

  const handleSortChange = (_sortBy) => {
    setSortBy(_sortBy);
  };

  const handlePageChange = (page) => setCurrentPage(page);
  const handleItemsPerPageChange = (num) => {
    setItemsPerPage(num);
  };

  const handleViewField = (groundId) => {
    navigate(`${routes.SLOTS}/${groundId}`);
  };

  const handleCreateField = () => {
    navigate(routes.SLOT_CREATE);
  };

  useEffect(() => {
    const params = buildApiParams();
    dispatch(generalExtraActions.getFields(params));
  }, [dispatch, buildApiParams]);

  const totalItems = fieldsPagination.count;
  const totalPages = Math.ceil(totalItems / itemsPerPage);

  if (fieldsError) {
    return (
      <div className="fields-page custom-listing">
        <div className="error-message">
          <h3>Error loading fields</h3>
          <p>{fieldsError?.message || 'Something went wrong. Please try again.'}</p>
          <button onClick={() => {
            const params = buildApiParams();
            dispatch(generalExtraActions.getGrounds(params));
          }}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fields-page custom-listing">
      {fieldsLoading && <Loader/>}
      <div className="listing-header">
        <div className="listing-title">Ground Fields:</div>
        <button className="listing-create-btn" onClick={handleCreateField}>Add new Field</button>
      </div>
      <div className="listing-filters">
        <Filters filters={filterOptions} selectedFilter={filter} handleFilter={handleFilter}/>
        <div className="filter-group">
          <SortBy options={sortOptions} value={sortBy} onChange={handleSortChange} />
          <SearchBar value={search} onChange={handleSearch} placeholder="Search fields..." debounceDelay={500} onDebouncedChange={handleSearch}/>
        </div>
      </div>
      <div className="listing-table-container">
        <table className="listing-table">
          <thead>
          <tr>
            <th>ID</th>
            <th>Name</th>
            <th>Ground</th>
            <th>Status</th>
            <th>Date Added</th>
            <th>Date Modified</th>
            <th>View</th>
          </tr>
          </thead>
          <tbody>
          {fields.length === 0 ? (
            <tr>
              <td colSpan="8" style={{textAlign: 'center', color: '#888'}}>No fields found.</td>
            </tr>
          ) : (
            fields.map((field, idx) => (
              <tr key={field.id}>
                <td>{field.id}</td>
                <td>{field.name}</td>
                <td>{field.ground.name}</td>
                <td>
                  <span
                    className={`badge badge-status${field.is_active ? '' : ' inactive'}`}>{field.is_active ? "Active" : "Inactive"}</span>
                </td>
                <td>{field.created ? new Date(field.created).toLocaleDateString() : 'N/A'}</td>
                <td>{field.modified ? new Date(field.modified).toLocaleDateString() : 'N/A'}</td>
                <td>
                  <button
                    className="view-btn"
                    onClick={() => handleViewField(field.id)}
                    title="View field details"
                  >
                    <FaRegEye/>
                  </button>
                </td>
              </tr>
            ))
          )}
          </tbody>
        </table>
      </div>
      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        itemsPerPage={itemsPerPage}
        totalItems={totalItems}
        onPageChange={handlePageChange}
        onItemsPerPageChange={handleItemsPerPageChange}
      />
    </div>
  );
}

export default Fields;